import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type { InvoiceDetailResponse } from '../../types/invoice';
import { mergePdfFiles, removePdfPageAtIndex0, reorderPdfPagesInFile, renderPdfPageDataUrlThumbnails } from '../../utils/invoicePdfPages';
import { fingerprintPdfFile, fingerprintRasterFiles } from '../../utils/invoiceDraftFingerprint';
import { fileKindFromFile, validateHomogeneousPageFiles, type InvoicePageFileKind } from '../../utils/invoicePageFileKinds';
import {
    isTiffFile,
    putInvoiceFileToSignedUrl,
    signedUrlKindForFile,
    TIFF_PREVIEW_PLACEHOLDER,
    type InvoiceSignedUrlKind,
} from '../../utils/invoiceUploadFiles';

const COMPRESSION_OPTIONS = {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1600,
    useWebWorker: true as const,
};

interface SignedUrlEntry {
    signedUrl: string;
    key: string;
}

interface GetSignedUrlsResponse {
    urls: SignedUrlEntry[];
}

function newDraftId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function uploadFileToS3(signedUrl: string, file: File, kind: InvoiceSignedUrlKind): Promise<void> {
    await putInvoiceFileToSignedUrl(signedUrl, file, kind);
}

async function compressIfRaster(file: File): Promise<File> {
    if (file.type === 'application/pdf' || isTiffFile(file)) return file;
    return imageCompression(file, COMPRESSION_OPTIONS);
}

/** Same logical assets as full URLs, but stable across presigned query-param rotation. */
function stableInvoiceMediaBootstrapKey(invoice: InvoiceDetailResponse | null | undefined): string {
    if (!invoice) return '';
    const urls = invoice.images?.length ? invoice.images : invoice.image ? [invoice.image] : [];
    if (urls.length === 0) return '';
    return urls
        .map((u) => {
            if (u.startsWith('data:') || u.startsWith('blob:')) return u;
            try {
                const parsed = new URL(u);
                return `${parsed.origin}${parsed.pathname}`;
            } catch {
                return u;
            }
        })
        .join('\0');
}

async function fetchUrlToFile(url: string, index: number): Promise<File> {
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) throw new Error('Could not download an invoice page.');
    const blob = await res.blob();
    const ext =
        blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
    const name = `page-${index + 1}.${ext}`;
    return new File([blob], name, { type: blob.type || `image/${ext === 'jpg' ? 'jpeg' : ext}` });
}

export interface UseInvoicePageReplacementArgs {
    invoice: InvoiceDetailResponse | null | undefined;
    invoiceId: string | undefined;
    isReadOnly: boolean;
}

type AddRasterPagesResult =
    | { ok: true; newTotal: number }
    | { ok: false; reason: string };

export function useInvoicePageReplacement({
    invoice,
    invoiceId,
    isReadOnly,
}: UseInvoicePageReplacementArgs) {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    const sessionId = sessionContext?.sessionId ?? '';
    const invoiceRef = useRef(invoice);
    invoiceRef.current = invoice;

    const [editMode, setEditMode] = useState(false);
    const [draftBootstrapping, setDraftBootstrapping] = useState(false);
    const [draftReloadKey, setDraftReloadKey] = useState(0);
    const [pdfDraft, setPdfDraft] = useState<File | null | undefined>(undefined);
    const [imageDraft, setImageDraft] = useState<File[] | undefined>(undefined);
    const [imageDraftIds, setImageDraftIds] = useState<string[] | undefined>(undefined);
    const [pdfThumbUrls, setPdfThumbUrls] = useState<string[]>([]);
    const [pdfPageIds, setPdfPageIds] = useState<string[]>([]);
    const [pdfThumbsLoading, setPdfThumbsLoading] = useState(false);
    const [rasterKind, setRasterKind] = useState<InvoicePageFileKind | null>(null);
    const [baselineFingerprint, setBaselineFingerprint] = useState('');
    const [error, setError] = useState<string | null>(null);
    const imageDraftRef = useRef<File[] | undefined>(undefined);
    const pagesAddedOrRemovedRef = useRef(false);

    useEffect(() => {
        imageDraftRef.current = imageDraft;
    }, [imageDraft]);

    const isPdfInvoice = useMemo(
        () =>
            invoice?.imageContentType === 'application/pdf' ||
            (invoice?.image?.startsWith('data:application/pdf') ?? false),
        [invoice?.image, invoice?.imageContentType]
    );

    const resetLocal = useCallback(() => {
        setEditMode(false);
        setPdfDraft(undefined);
        setImageDraft(undefined);
        setImageDraftIds(undefined);
        setPdfThumbUrls([]);
        setPdfPageIds([]);
        setPdfThumbsLoading(false);
        setRasterKind(null);
        setBaselineFingerprint('');
        setError(null);
        pagesAddedOrRemovedRef.current = false;
    }, []);

    const draftMediaBootstrapKey = useMemo(() => stableInvoiceMediaBootstrapKey(invoice), [invoice]);

    useEffect(() => {
        resetLocal();
    }, [invoiceId, resetLocal]);

    const loadDraftFromInvoice = useCallback(
        async (inv: InvoiceDetailResponse, isStale: () => boolean) => {
            if (isReadOnly) return;
            if (!isStale()) {
                setDraftBootstrapping(true);
                setError(null);
            }
            try {
                const isPdf =
                    inv.imageContentType === 'application/pdf' ||
                    (inv.image?.startsWith('data:application/pdf') ?? false);
                if (isPdf) {
                    if (!inv.image) throw new Error('Missing PDF URL.');
                    const res = await fetch(inv.image, { mode: 'cors', cache: 'no-store' });
                    if (isStale()) return;
                    if (!res.ok) throw new Error('Could not load the invoice PDF.');
                    const blob = await res.blob();
                    if (isStale()) return;
                    const file = new File([blob], 'invoice.pdf', { type: 'application/pdf' });
                    setPdfDraft(file);
                    setBaselineFingerprint(fingerprintPdfFile(file));
                    setImageDraft(undefined);
                    setImageDraftIds(undefined);
                } else {
                    const urls = inv.images?.length ? inv.images : inv.image ? [inv.image] : [];
                    if (urls.length === 0) throw new Error('No invoice images to edit.');
                    const files = await Promise.all(urls.map((u, i) => fetchUrlToFile(u, i)));
                    if (isStale()) return;
                    const k = fileKindFromFile(files[0]!);
                    setRasterKind(k);
                    setImageDraft(files);
                    setImageDraftIds(files.map(() => newDraftId()));
                    setBaselineFingerprint(fingerprintRasterFiles(files));
                    setPdfDraft(undefined);
                }
                if (!isStale()) setEditMode(true);
            } catch (e) {
                if (!isStale()) {
                    setError(e instanceof Error ? e.message : 'Could not load invoice files for editing.');
                    setEditMode(false);
                }
            } finally {
                if (!isStale()) setDraftBootstrapping(false);
            }
        },
        [isReadOnly]
    );

    useEffect(() => {
        if (!invoiceId || isReadOnly || !draftMediaBootstrapKey) return;
        if (editMode) return;
        const inv = invoiceRef.current;
        if (!inv) return;
        let cancelled = false;
        const isStale = () => cancelled;
        void loadDraftFromInvoice(inv, isStale);
        return () => {
            cancelled = true;
        };
    }, [invoiceId, isReadOnly, draftMediaBootstrapKey, draftReloadKey, editMode, loadDraftFromInvoice]);

    useEffect(() => {
        let cancelled = false;
        if (!editMode || !isPdfInvoice || !pdfDraft) {
            setPdfThumbUrls([]);
            setPdfPageIds([]);
            setPdfThumbsLoading(false);
            return;
        }
        setPdfThumbsLoading(true);
        (async () => {
            try {
                if (isTiffFile(pdfDraft)) {
                    if (cancelled) return;
                    setPdfThumbUrls([TIFF_PREVIEW_PLACEHOLDER]);
                    setPdfPageIds([newDraftId()]);
                    return;
                }
                const thumbs = await renderPdfPageDataUrlThumbnails(pdfDraft);
                if (cancelled) return;
                setPdfThumbUrls(thumbs);
                setPdfPageIds(thumbs.map(() => newDraftId()));
            } catch {
                if (!cancelled) {
                    setPdfThumbUrls([]);
                    setPdfPageIds([]);
                    setError('Could not render PDF page previews.');
                }
            } finally {
                if (!cancelled) setPdfThumbsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [editMode, isPdfInvoice, pdfDraft]);

    const cancelEditMode = useCallback(() => {
        resetLocal();
        setDraftReloadKey((k) => k + 1);
    }, [resetLocal]);

    const markPagesAddedOrRemoved = useCallback(() => {
        pagesAddedOrRemovedRef.current = true;
    }, []);

    const startOver = useCallback(() => {
        if (!editMode) return;
        markPagesAddedOrRemoved();
        setError(null);
        if (isPdfInvoice) {
            setPdfDraft(null);
            setBaselineFingerprint(fingerprintPdfFile(null));
        } else {
            setImageDraft([]);
            setImageDraftIds([]);
            setRasterKind(null);
            setBaselineFingerprint(fingerprintRasterFiles([]));
        }
    }, [editMode, isPdfInvoice, markPagesAddedOrRemoved]);

    const removeImageAt = useCallback((index: number) => {
        markPagesAddedOrRemoved();
        setImageDraft((prev) => {
            if (!prev) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
        setImageDraftIds((prev) => {
            if (!prev) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    }, [markPagesAddedOrRemoved]);

    const removePdfAt = useCallback(async (index0: number) => {
        if (!pdfDraft) return;
        try {
            const next = await removePdfPageAtIndex0(pdfDraft, index0);
            markPagesAddedOrRemoved();
            setPdfDraft(next);
            if (next == null) {
                setBaselineFingerprint(fingerprintPdfFile(null));
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not remove that page.');
        }
    }, [pdfDraft, markPagesAddedOrRemoved]);

    const reorderImages = useCallback((from: number, to: number) => {
        setImageDraft((prev) => (prev ? arrayMove(prev, from, to) : prev));
        setImageDraftIds((prev) => (prev ? arrayMove(prev, from, to) : prev));
    }, []);

    const reorderPdf = useCallback(
        async (from: number, to: number) => {
            if (!pdfDraft) return;
            try {
                const next = await reorderPdfPagesInFile(pdfDraft, from, to);
                setPdfDraft(next);
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Could not reorder pages.');
            }
        },
        [pdfDraft]
    );

    const addRasterPages = useCallback((files: File[]): AddRasterPagesResult => {
        if (!files.length) return { ok: false, reason: 'No files were selected.' };
        const msg = validateHomogeneousPageFiles(files, rasterKind ?? undefined);
        if (msg) {
            setError(msg);
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.warn('[invoice page replace] add rejected:', msg, {
                    files: files.map((f) => ({ name: f.name, type: f.type })),
                    existingKind: rasterKind,
                });
            }
            return { ok: false, reason: msg };
        }
        const firstKind = fileKindFromFile(files[0]!);
        if (firstKind === 'pdf') {
            const reason = 'Add images only for this invoice (PDF invoices use Add PDF).';
            setError(reason);
            return { ok: false, reason };
        }
        setError(null);
        markPagesAddedOrRemoved();
        setRasterKind((prev) => prev ?? firstKind);
        const newIds = files.map(() => newDraftId());
        const existingCount = imageDraftRef.current?.length ?? 0;
        const newPageCount = existingCount + files.length;
        setImageDraft((prev: File[] | undefined) => {
            return [...(prev ?? []), ...files];
        });
        setImageDraftIds((prev: string[] | undefined) => [...(prev ?? []), ...newIds]);
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.info('[invoice page replace] appended', files.length, 'file(s); total pages', newPageCount);
        }
        return { ok: true, newTotal: newPageCount };
    }, [rasterKind, markPagesAddedOrRemoved]);

    const appendPdfPages = useCallback(async (file: File) => {
        try {
            if (isTiffFile(file)) {
                if (pdfDraft && !isTiffFile(pdfDraft)) {
                    setError('A TIF file must replace the whole document. Use Start over, then add the TIF.');
                    return;
                }
                setError(null);
                markPagesAddedOrRemoved();
                setPdfDraft(file);
                return;
            }
            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                setError('Only PDF or TIF files can be added here.');
                return;
            }
            if (pdfDraft && isTiffFile(pdfDraft)) {
                setError('Cannot merge a PDF into a TIF. Use Start over to switch files.');
                return;
            }
            setError(null);
            if (!pdfDraft) {
                markPagesAddedOrRemoved();
                setPdfDraft(file);
                return;
            }
            const merged = await mergePdfFiles(pdfDraft, file);
            markPagesAddedOrRemoved();
            setPdfDraft(merged);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not add PDF pages.');
        }
    }, [pdfDraft, markPagesAddedOrRemoved]);

    const hasPages = useMemo(() => {
        if (!editMode) return true;
        if (isPdfInvoice) return pdfDraft != null;
        return (imageDraft?.length ?? 0) > 0;
    }, [editMode, isPdfInvoice, pdfDraft, imageDraft]);

    const currentFingerprint = useMemo(() => {
        if (!editMode) return '';
        if (isPdfInvoice) {
            if (pdfDraft === undefined) return '';
            return fingerprintPdfFile(pdfDraft);
        }
        if (imageDraft === undefined) return '';
        return fingerprintRasterFiles(imageDraft);
    }, [editMode, isPdfInvoice, pdfDraft, imageDraft]);

    const hasDraftChangesVsBaseline = editMode && currentFingerprint !== baselineFingerprint;

    const previewImageUrls = useMemo(() => {
        if (!editMode || isPdfInvoice || !imageDraft) return null;
        return imageDraft.map((f) => (isTiffFile(f) ? TIFF_PREVIEW_PLACEHOLDER : URL.createObjectURL(f)));
    }, [editMode, isPdfInvoice, imageDraft]);

    const previewPdfUrl = useMemo(() => {
        if (!editMode || !isPdfInvoice || pdfDraft === undefined) return null;
        if (!pdfDraft) return null;
        if (isTiffFile(pdfDraft)) return null;
        return URL.createObjectURL(pdfDraft);
    }, [editMode, isPdfInvoice, pdfDraft]);

    useEffect(() => {
        return () => {
            if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
            if (previewImageUrls) {
                previewImageUrls.forEach((u) => {
                    if (u.startsWith('blob:')) URL.revokeObjectURL(u);
                });
            }
        };
    }, [previewPdfUrl, previewImageUrls]);

    const stripThumbnailUrls = useMemo(() => {
        if (!editMode) return [];
        if (isPdfInvoice) return pdfThumbUrls;
        return previewImageUrls ?? [];
    }, [editMode, isPdfInvoice, pdfThumbUrls, previewImageUrls]);

    const stripSortableIds = useMemo(() => {
        if (!editMode) return [];
        if (isPdfInvoice) return pdfPageIds;
        return imageDraftIds ?? [];
    }, [editMode, isPdfInvoice, pdfPageIds, imageDraftIds]);

    const stripDisplay = useMemo(() => {
        const u = stripThumbnailUrls;
        const id = stripSortableIds;
        const n = Math.min(u.length, id.length);
        return { urls: u.slice(0, n), ids: id.slice(0, n) };
    }, [stripThumbnailUrls, stripSortableIds]);

    const modalPreviewUrls = useMemo(() => {
        if (!editMode) return [];
        if (isPdfInvoice) return pdfThumbUrls;
        return previewImageUrls ?? [];
    }, [editMode, isPdfInvoice, pdfThumbUrls, previewImageUrls]);

    const replaceMutation = useMutation({
        mutationFn: async () => {
            if (!sessionId) throw new Error('Your session expired. Please sign in again.');
            if (!invoiceId || !invoice?.locationId) throw new Error('Missing invoice or location.');
            let imageFiles: File[] | undefined;
            let pdfFile: File | undefined;
            if (isPdfInvoice) {
                if (!pdfDraft) throw new Error('Add a PDF before re-processing.');
                pdfFile = pdfDraft;
            } else {
                if (!imageDraft?.length) throw new Error('Add at least one image before re-processing.');
                imageFiles = await Promise.all(imageDraft.map((f) => compressIfRaster(f)));
            }
            const filesToUpload = pdfFile ? [pdfFile] : imageFiles!;
            const sequence: InvoiceSignedUrlKind[] = [];
            for (const file of filesToUpload) {
                const kind = signedUrlKindForFile(file);
                if (kind == null) {
                    throw new Error('Only JPG, PNG, TIF, WebP, and PDF files are supported.');
                }
                sequence.push(kind);
            }
            const headers = { Authorization: sessionId, 'Content-Type': 'application/json' };
            const signedUrlsRes = await axios.post(
                `${API_URL}/invoice/get-signed-urls`,
                { sequence },
                { headers, validateStatus: () => true }
            );
            if (signedUrlsRes.status !== 200) {
                const err = (signedUrlsRes.data as { error?: string })?.error ?? 'Failed to get upload URLs';
                throw new Error(err);
            }
            const { urls } = signedUrlsRes.data as GetSignedUrlsResponse;
            if (!urls || urls.length !== filesToUpload.length) {
                throw new Error('Unexpected number of upload URLs returned');
            }
            await Promise.all(
                filesToUpload.map((file, i) => uploadFileToS3(urls[i]!.signedUrl, file, sequence[i]!)),
            );
            const s3Keys = urls.map((u) => u.key);
            const uploadRes = await axios.post(
                `${API_URL}/invoice/replace-pages`,
                {
                    invoiceId,
                    s3Keys,
                    locationId: invoice.locationId,
                    ...(pagesAddedOrRemovedRef.current ? { pagesAddedOrRemoved: true } : {}),
                },
                { headers, validateStatus: () => true }
            );
            if (uploadRes.status !== 200) {
                const body = uploadRes.data as { error?: string; details?: string };
                throw new Error(body?.details ?? body?.error ?? 'Replace upload failed');
            }
            return (uploadRes.data as { uuid: string }).uuid;
        },
        onSuccess: async (uuid) => {
            queryClient.invalidateQueries({ queryKey: ['/invoice/activity-logs', invoiceId] });
            resetLocal();
            for (let i = 0; i < 45; i += 1) {
                await new Promise((r) => setTimeout(r, 2000));
                const { data } = await axios.post(
                    `${API_URL}/invoice/processing-status`,
                    { uuid },
                    { headers: { Authorization: sessionId } }
                );
                const status = data.status as string;
                if (status === 'quality_failed') {
                    throw new Error(
                        (data as { error?: string }).error ?? 'Processing failed (quality or RO detection).'
                    );
                }
                if (status === 'processing_complete') {
                    await queryClient.invalidateQueries({ queryKey: ['/invoice/detail', invoiceId] });
                    setDraftReloadKey((k) => k + 1);
                    return;
                }
            }
            throw new Error('Processing is taking longer than expected. Refresh the page in a moment.');
        },
    });

    const blocksNexSubmit = !draftBootstrapping && hasDraftChangesVsBaseline;
    const blocksNexSubmitReason = blocksNexSubmit
        ? 'Confirm or cancel page changes before submitting this invoice to NEX.'
        : undefined;

    return {
        editMode,
        draftBootstrapping,
        cancelEditMode,
        startOver,
        removeImageAt,
        removePdfAt,
        reorderImages,
        reorderPdf,
        addRasterPages,
        appendPdfPages,
        hasPages,
        error,
        setError,
        isPdfInvoice,
        replaceMutation,
        resetLocal,
        previewPdfUrl,
        previewImageUrls,
        stripThumbnailUrls,
        stripSortableIds,
        stripDisplayUrls: stripDisplay.urls,
        stripDisplayIds: stripDisplay.ids,
        pdfThumbsLoading,
        blocksNexSubmit,
        blocksNexSubmitReason,
        hasDraftChangesVsBaseline,
        modalPreviewUrls,
        rasterKind,
    };
}
